import { Component, OnInit, inject, ChangeDetectionStrategy, DestroyRef, ChangeDetectorRef, signal, computed } from '@angular/core';

import { UpdateService } from '../../features/specialized/services/update.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface FooterVersion {
    Number?: string | null;
    Build?: string | null;
    Date?: string | null;
}

@Component({
    selector: 'app-footer',
    imports: [],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
<div class="stela-footer-bar w-full border-t py-4 px-6 z-10 flex flex-wrap items-center justify-between gap-2 text-sm"
     style="background-color: var(--stela-surface); border-color: var(--stela-line); color: var(--stela-text-secondary)">
    <div class="font-medium min-w-0">
        <strong>Copyright</strong> Diaple Networking SL &copy; <span class="whitespace-nowrap">{{currentYear}}</span>
    </div>
    @if (versionLabel(); as label) {
        <div class="min-w-0 grow text-right">
            <a (click)="showVersion()" class="inline-block max-w-full text-right cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap transition-colors no-underline" style="color: var(--stela-text-secondary)">
                {{ label }}
            </a>
        </div>
    }
</div>
    `
})
export class FooterComponent implements OnInit {
    private updateService = inject(UpdateService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly cdRef = inject(ChangeDetectorRef);

    currentYear = new Date().getFullYear();
    actualVersion = signal<FooterVersion | null>(null);

    /**
     * Render `Versión X - Build: Y - Z` with any subset of those tokens present,
     * and render nothing at all if every token is empty (was showing
     * "Versión  - Build:  - " in environments where the API responds with
     * blanks).
     */
    versionLabel = computed<string | null>(() => {
        const v = this.actualVersion();
        if (!v) {
            return null;
        }
        const parts: string[] = [];
        if (v.Number) parts.push(`Versión ${v.Number}`);
        if (v.Build) parts.push(`Build: ${v.Build}`);
        if (v.Date) parts.push(v.Date);
        return parts.length > 0 ? parts.join(' - ') : null;
    });

    ngOnInit(): void {
        this.updateService.checkForUpdates().pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe({
            next: (version) => {
                this.actualVersion.set(version as FooterVersion);
                this.cdRef.markForCheck();
            },
            error: () => {
                this.actualVersion.set({
                    Number: '1.0.0',
                    Build: 'Dev',
                    Date: new Date().toLocaleDateString()
                });
                this.cdRef.markForCheck();
            }
        });
    }

    showVersion(): void {
        // Version modal - could be implemented with PrimeNG dialog later
        // For now, this is a placeholder for future implementation
    }
}
